import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, CardHeader, EmptyState, Metric, Select, Tabs,
} from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import useReportingPage from '../hooks/useReportingPage';
import CompetitiveNetworkGraph from './competitive/CompetitiveNetworkGraph';
import CompetitiveHeadToHead from './competitive/CompetitiveHeadToHead';
import CompetitiveSidePanel from './competitive/CompetitiveSidePanel';
import CompetitiveDetailsTable from './competitive/CompetitiveDetailsTable';
import { COMPETITOR_COLOR, YOURS_COLOR } from './competitive/competitiveUtils';

const axios = require('axios').default;

const ALL_VENUES = 'all';
const ROSTR_POLL_MS = 20000;
const ROSTR_MAX_POLLS = 10;
const VIEWS = [{ value: 'network', label: 'Network' }, { value: 'bars', label: 'Head-to-head' }];
const LEVELS = [{ value: 'agency', label: 'Agencies' }, { value: 'agent', label: 'Agents' }];
const noop = () => {};
const toolbarLabel = { fontSize: 13, color: 'var(--sl-text-tertiary)', fontWeight: 500, whiteSpace: 'nowrap' };
const dot = (color, size = 8) => ({ width: size, height: size, borderRadius: '50%', background: color, flex: 'none' });
const isoDaysAgo = (days) => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);

const venueOption = (venue) => ({
  value: String(venue.id),
  label: [venue.name, venue.city].filter(Boolean).join(' · '),
});

function kpiCards(report) {
  const kpis = report.kpis;
  const yourName = report.your_venue.name;
  const competitorName = report.competitor?.name || 'competitor';
  const gapNote = (count) => (
    <span style={{ fontSize: 12, color: count ? 'var(--sl-text-error)' : 'var(--sl-text-tertiary)' }}>{`${count} not booking with you`}</span>
  );
  const note = (text) => <span style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{text}</span>;
  const coverage = kpis.competitor_shows ? Math.round((kpis.competitor_shows_with_agency / kpis.competitor_shows) * 100) : 0;
  return [
    { label: `Shows at ${competitorName}`, value: kpis.competitor_shows, footer: note(`${yourName}: ${kpis.your_shows} · ${coverage}% with a known agency`) },
    { label: `Agencies at ${competitorName}`, value: kpis.competitor_agencies, footer: gapNote(kpis.agency_gaps) },
    { label: `Agents at ${competitorName}`, value: kpis.competitor_agents, footer: gapNote(kpis.agent_gaps) },
    {
      label: 'Shared agents',
      value: kpis.shared_agents,
      footer: note(`${kpis.competitor_agents ? Math.round((kpis.shared_agents / kpis.competitor_agents) * 100) : 0}% of their agents also book you`),
    },
  ];
}

function ReportingBookingCompetitiveVenuesPage({ team, venueOwnerships }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen, dateRange, setDateRange,
  } = useReportingPage('today', { defaultStart: isoDaysAgo(365) });

  const [venueOwnershipId, setVenueOwnershipId] = useState(venueOwnerships?.length === 1 ? String(venueOwnerships[0].id) : ALL_VENUES);
  const [competitorId, setCompetitorId] = useState(null);
  const [competitorOptions, setCompetitorOptions] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('network');
  const [level, setLevel] = useState('agency');
  const [selectedKey, setSelectedKey] = useState(null);
  const [rostrQueued, setRostrQueued] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const rostrRequested = useRef(null);
  const polls = useRef(0);

  const base = `/teams/${team?.id}/reporting/booking/competitive_venues`;
  const venueParams = venueOwnershipId === ALL_VENUES ? {} : { venue_ownership_id: [venueOwnershipId] };
  const params = useMemo(() => ({
    ...venueParams,
    start_date: dateRange?.start,
    end_date: dateRange?.end,
    ...(competitorId ? { competitor_venue_id: competitorId } : {}),
  }), [venueOwnershipId, competitorId, dateRange]);

  useEffect(() => {
    if (!team?.id) return undefined;
    let active = true;
    axios.get(`${base}/filter_options.json`, { params: venueParams })
      .then((res) => {
        if (!active) return;
        setCompetitorOptions((res.data?.competitor_venues || []).map(venueOption));
        setCompetitorId(null);
      })
      .catch((err) => console.error(err));
    return () => { active = false; };
  }, [team?.id, venueOwnershipId]);

  useEffect(() => {
    if (!team?.id) return undefined;
    let active = true;
    if (refreshTick === 0) setLoading(true);
    axios.get(`${base}/data.json`, { params })
      .then((res) => {
        if (!active) return;
        setReport(res.data);
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError('Failed to load competitive venues.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params, refreshTick]);

  useEffect(() => {
    setSelectedKey(null);
    setRefreshTick(0);
    polls.current = 0;
  }, [params]);

  useEffect(() => {
    const rostr = report?.rostr;
    if (!rostr?.available || !rostr.pending) return undefined;
    const requestKey = JSON.stringify(params);
    if (rostrRequested.current !== requestKey) {
      rostrRequested.current = requestKey;
      axios.post(`${base}/refresh_representation.json`, params)
        .then((res) => setRostrQueued(res.data?.queued || 0))
        .catch((err) => console.error(err));
    }
    if (polls.current >= ROSTR_MAX_POLLS) return undefined;
    const timer = setTimeout(() => {
      polls.current += 1;
      setRefreshTick((tick) => tick + 1);
    }, ROSTR_POLL_MS);
    return () => clearTimeout(timer);
  }, [report]);

  const loadCompetitors = (query) => {
    if (!query) return Promise.resolve(competitorOptions);
    return axios.get('/venues/search', { params: { name: query } })
      .then((res) => (res.data || []).map((venue) => ({ value: String(venue.id), label: [venue.name, venue.city].filter(Boolean).join(' · ') })))
      .catch((err) => {
        console.error(err);
        return [];
      });
  };

  const yourName = report?.your_venue?.name || 'Your venue';
  const competitorName = report?.competitor?.name || 'Competitor';
  const rows = report ? (level === 'agency' ? report.agencies : report.agents) : [];
  const selectedRow = selectedKey && rows.find((row) => row.key === selectedKey);
  const selection = selectedRow ? { level, key: selectedKey } : null;
  const pickRow = (pickLevel, key) => {
    setLevel(pickLevel);
    setSelectedKey(key);
  };
  const competitorValue = competitorId || (report?.competitor ? String(report.competitor.id) : null);
  const selectOptions = report?.competitor && !competitorOptions.some((option) => option.value === String(report.competitor.id))
    ? [...competitorOptions, venueOption(report.competitor)]
    : competitorOptions;

  const toolbarExtras = (
    <div className="d-flex align-items-center flex-wrap w-100" style={{ gap: 12 }}>
      <span className="d-flex align-items-center" style={{ ...toolbarLabel, gap: 6 }}><span style={dot(YOURS_COLOR)} />Your venue</span>
      <Select
        size="sm"
        width={220}
        options={[{ value: ALL_VENUES, label: 'All your venues' }, ...(venueOwnerships || []).map((vo) => ({ value: String(vo.id), label: vo.name }))]}
        value={venueOwnershipId}
        onChange={(value) => setVenueOwnershipId(value || ALL_VENUES)}
      />
      <span style={{ ...toolbarLabel, textTransform: 'uppercase', letterSpacing: '.06em', fontSize: 12 }}>vs</span>
      <span className="d-flex align-items-center" style={{ ...toolbarLabel, gap: 6 }}><span style={dot(COMPETITOR_COLOR)} />Competitor</span>
      <Select
        size="sm"
        width={260}
        searchable
        placeholder="Search venues"
        options={selectOptions}
        loadOptions={loadCompetitors}
        value={competitorValue}
        onChange={(value) => value && setCompetitorId(value)}
        noOptionsMessage="No venues with shows nearby. Search by name."
      />
    </div>
  );

  const rostrPending = report?.rostr?.available ? report.rostr.pending : 0;

  return (
    <ReportingPageLayout
      team={team}
      activeKey="booking_competitive_venues"
      title="Competitive venues"
      subtitle="See which agents and agencies are booking your competition — and where your relationships are thin."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      showFilters={false}
      activeFilters={{}}
      filterGroups={[]}
      filterSidebarOpen={false}
      setFilterSidebarOpen={noop}
      handleFilterChange={noop}
      handleRemoveFilter={noop}
      handleClearAllFilters={noop}
      toolbarExtras={toolbarExtras}
      headerActions={(
        <Button variant="secondary" size="sm" href={`${base}/export?${new URLSearchParams(Object.entries(params).flatMap(([key, value]) => (Array.isArray(value) ? value.map((v) => [`${key}[]`, v]) : [[key, value]]))).toString()}`} as="a">
          Export CSV
        </Button>
      )}
    >
      {rostrPending > 0 && (
        <div className="mb-3">
          <Alert
            color="brand"
            title={`Looking up agents for ${rostrQueued || rostrPending} ${(rostrQueued || rostrPending) === 1 ? 'artist' : 'artists'} via ROSTR`}
            description="This page refreshes on its own as results come in, usually within a few minutes."
          />
        </div>
      )}

      {!loading && report && !report.competitor && (
        <Card>
          <EmptyState title="Pick a competitor" description="We couldn't find a venue with shows near yours. Search for a competitor by name above." />
        </Card>
      )}

      {report?.competitor && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: 16, marginBottom: 16 }}>
            {kpiCards(report).map((card) => (
              <Metric key={card.label} label={card.label} value={loading ? '—' : card.value.toLocaleString()} footer={card.footer} />
            ))}
          </div>

          <div className="mb-3">
            <Card
              padding="none"
              header={(
                <CardHeader
                  title="Booking network"
                  supportingText={level === 'agency'
                    ? 'Every agency that booked either room. Click one to see its agents.'
                    : 'Every agent that booked either room. Click one to see their shows.'}
                  actions={(
                    <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                      <Tabs variant="button-border" size="sm" collapseBelow={0} items={VIEWS} value={view} onChange={setView} />
                      <Tabs variant="button-border" size="sm" collapseBelow={0} items={LEVELS} value={level} onChange={(value) => { setLevel(value); setSelectedKey(null); }} />
                    </div>
                  )}
                />
              )}
            >
              <div className="d-flex flex-wrap">
                <div className="d-flex flex-column" style={{ flex: '999 1 720px', minWidth: 0, padding: '16px 24px 20px', gap: 12 }}>
                  <div className="d-flex flex-wrap" style={{ gap: 20, fontSize: 12, color: 'var(--sl-text-tertiary)' }}>
                    <span className="d-flex align-items-center" style={{ gap: 6 }}><span style={dot(YOURS_COLOR, 10)} />{`Books with ${yourName}`}</span>
                    <span className="d-flex align-items-center" style={{ gap: 6 }}><span style={dot(COMPETITOR_COLOR, 10)} />{`Books with ${competitorName}`}</span>
                    <span className="d-flex align-items-center" style={{ gap: 6 }}><span style={{ ...dot('transparent', 14), border: '1.5px dashed var(--sl-fg-error)' }} />Never booked with you</span>
                    {view === 'network' && <span>Position = share of shows · Size = total shows · Line = shows at venue</span>}
                  </div>
                  {rows.length === 0 && !loading && (
                    <EmptyState size="sm" title="No agent data in this range" description="None of these shows have a known agency yet. Try a wider date range." />
                  )}
                  {rows.length > 0 && view === 'network' && (
                    <CompetitiveNetworkGraph
                      rows={rows}
                      level={level}
                      yourName={yourName}
                      competitorName={competitorName}
                      yourShows={report.kpis.your_shows}
                      competitorShows={report.kpis.competitor_shows}
                      selectedKey={selectedKey}
                      onSelect={setSelectedKey}
                    />
                  )}
                  {rows.length > 0 && view === 'bars' && (
                    <CompetitiveHeadToHead
                      rows={rows}
                      level={level}
                      yourName={yourName}
                      competitorName={competitorName}
                      selectedKey={selectedKey}
                      onSelect={setSelectedKey}
                    />
                  )}
                </div>
                <CompetitiveSidePanel
                  selectedRow={selectedRow}
                  level={level}
                  agents={report.agents}
                  events={report.events}
                  yourName={yourName}
                  competitorName={competitorName}
                  onPickAgent={(key) => pickRow('agent', key)}
                  onClear={() => setSelectedKey(null)}
                />
              </div>
            </Card>
          </div>

          <div style={{ paddingBottom: 100 }}>
            <CompetitiveDetailsTable
              report={report}
              loading={loading}
              error={error}
              selection={selection}
              selectedRow={selectedRow}
              onPick={pickRow}
              onClearSelection={() => setSelectedKey(null)}
            />
          </div>
        </>
      )}

      {error && !report && <Alert color="error" title={error} />}
    </ReportingPageLayout>
  );
}

export default ReportingBookingCompetitiveVenuesPage;
